import React, { useEffect, useState } from 'react';
import { ArrowLeft, Settings, Type, Clock, X, CornerDownLeft } from 'lucide-react';

const TypingView = ({
    articleTitle,
    difficulty,
    userInput,
    parsedData,
    cursorStyle,
    onTyping,
    onGoHome,
    inputRef,
    containerRef,
    charRefs,
    currentTypingWordId,
    handleWordClick,
    xtraData
}) => {
    const [elapsedTime, setElapsedTime] = useState(0);
    const [liveWpm, setLiveWpm] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (elapsedTime > 0) {
            const wordsTyped = userInput.length / 5;
            const minutes = elapsedTime / 60;
            setLiveWpm(Math.round(wordsTyped / minutes));
        }
    }, [userInput, elapsedTime]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-8 font-sans flex flex-col items-center transition-colors">
            <div className="max-w-4xl w-full space-y-6">
                <div className="flex justify-between items-start px-2">
                    <div>
                        <button onClick={onGoHome} className="flex items-center gap-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 mb-2 transition-colors group">
                            <div className="p-1 bg-slate-100 dark:bg-slate-800 rounded-full group-hover:bg-slate-200 dark:group-hover:bg-slate-700 transition-colors"><ArrowLeft className="w-4 h-4" /></div>
                            <span className="text-sm font-medium">結束練習</span>
                        </button>
                        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{articleTitle || '專注打字'}</h2>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 dark:text-slate-400">
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded font-bold uppercase text-xs ${difficulty === 'hard' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'}`}>
                                <Settings className="w-3 h-3" /> {difficulty === 'hard' ? 'Hard' : 'Easy'}
                            </span>
                            <span className="flex items-center gap-1"><Type className="w-3 h-3" /> {parsedData.length} 字元</span>
                        </div>
                    </div>
                    <div className="flex gap-6 text-right">
                        <div className="flex flex-col items-end">
                            <span className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">Time</span>
                            <span className="font-mono text-xl font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-indigo-500" />
                                {formatTime(elapsedTime)}
                            </span>
                        </div>
                        <div className="flex flex-col items-end">
                            <span className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">WPM</span>
                            <span className="font-mono text-xl font-bold text-indigo-600 dark:text-indigo-400">{liveWpm}</span>
                        </div>
                    </div>
                </div>

                <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-800 p-8 min-h-[400px] transition-colors" onClick={() => inputRef.current?.focus()}>
                    <textarea
                        ref={inputRef}
                        value={userInput}
                        onChange={onTyping}
                        className="absolute opacity-0 top-0 left-0 w-full h-full z-10 cursor-text resize-none"
                        autoFocus
                        spellCheck="false"
                    />

                    <div ref={containerRef} className="text-2xl md:text-3xl leading-loose tracking-wide text-slate-300 dark:text-slate-600 font-mono whitespace-pre-wrap break-words relative z-20 select-none" style={{ fontFamily: '"Roboto Mono", monospace' }}>
                        {difficulty === 'xtra' ? (
                            // Xtra Mode: Sentence Blocks
                            (() => {
                                const blocks = [];
                                let currentSId = -1;
                                let currentBlockItems = [];

                                parsedData.forEach((item, index) => {
                                    if (item.sentenceId !== undefined && item.sentenceId !== currentSId) {
                                        if (currentBlockItems.length > 0) {
                                            blocks.push({ id: currentSId, items: currentBlockItems });
                                        }
                                        currentSId = item.sentenceId;
                                        currentBlockItems = [];
                                    }
                                    currentBlockItems.push({ ...item, originalIndex: index });
                                });
                                if (currentBlockItems.length > 0) {
                                    blocks.push({ id: currentSId, items: currentBlockItems });
                                }

                                return blocks.map(block => {
                                    const translation = (xtraData && xtraData[block.id]) ? xtraData[block.id] : null;

                                    // Check if cursor is in this block
                                    const isCursorInBlock = block.items.some(item => item.originalIndex === userInput.length);

                                    return (
                                        <div key={block.id} className="mb-8 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                            {/* Translation Header */}
                                            {translation && (
                                                <div className="mb-3 text-lg text-cyan-700 dark:text-cyan-400 font-bold leading-relaxed">
                                                    {translation}
                                                </div>
                                            )}

                                            {/* Separator */}
                                            <div className="h-px bg-slate-300 dark:bg-slate-600 opacity-40 mb-4 w-full" />

                                            {/* English Content */}
                                            <div className="relative">
                                                {block.items.map((item) => {
                                                    const index = item.originalIndex;
                                                    let statusClass = 'text-slate-300 dark:text-slate-600';
                                                    if (index < userInput.length) {
                                                        statusClass = userInput[index] === item.char ? 'text-slate-800 dark:text-slate-200' : 'text-red-500 dark:text-red-400 bg-red-100 dark:bg-red-900/30 rounded';
                                                    } else if (index === userInput.length) {
                                                        statusClass = 'text-slate-400 dark:text-slate-500 bg-indigo-100 dark:bg-indigo-900/30 rounded';
                                                    }

                                                    const isCurrentWord = item.wordId === currentTypingWordId;
                                                    const wordClass = (isCurrentWord && item.wordId !== -1) ? 'bg-white dark:bg-slate-700 shadow-sm' : '';

                                                    if (item.isNewline) {
                                                        return (
                                                            <span key={index} className="relative" ref={el => charRefs.current[index] = el}>
                                                                <span className={`${statusClass} ${wordClass} select-none inline-block px-1`}>
                                                                    <CornerDownLeft className="w-4 h-4 inline opacity-40" />
                                                                </span>
                                                                {'\n'}
                                                            </span>
                                                        );
                                                    }

                                                    return (
                                                        <span
                                                            key={index}
                                                            ref={el => charRefs.current[index] = el}
                                                            className={`${statusClass} ${wordClass} transition-colors duration-75 rounded-sm inline-block`}
                                                            onClick={(e) => { e.stopPropagation(); handleWordClick(item.wordId); }}
                                                        >
                                                            {(index < userInput.length && userInput[index] === item.char) ? item.char : (item.displayChar || item.char)}
                                                        </span>
                                                    );
                                                })}

                                                {/* Cursor for this block */}
                                                {isCursorInBlock && (
                                                    <div
                                                        className="absolute bg-indigo-500 dark:bg-indigo-400 w-[2px] transition-all duration-75 ease-out z-20 animate-pulse pointer-events-none"
                                                        style={{
                                                            left: cursorStyle.left,
                                                            top: cursorStyle.top,
                                                            height: cursorStyle.height
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                });
                            })()
                        ) : (
                            // Standard Mode (Original Logic)
                            parsedData.map((item, index) => {
                                let statusClass = 'text-slate-300 dark:text-slate-600';
                                if (index < userInput.length) {
                                    statusClass = userInput[index] === item.char ? 'text-slate-800 dark:text-slate-200' : 'text-red-500 dark:text-red-400 bg-red-100 dark:bg-red-900/30 rounded';
                                } else if (index === userInput.length) {
                                    statusClass = 'text-slate-400 dark:text-slate-500 bg-indigo-100 dark:bg-indigo-900/30 rounded';
                                }

                                // Highlight current word logic
                                const isCurrentWord = item.wordId === currentTypingWordId;
                                const wordClass = (isCurrentWord && item.wordId !== -1) ? 'bg-slate-50 dark:bg-slate-800/50' : '';

                                if (item.isNewline) {
                                    return (
                                        <span key={index} className="relative" ref={el => charRefs.current[index] = el}>
                                            <span className={`${statusClass} ${wordClass} select-none inline-block px-1`}>
                                                <CornerDownLeft className="w-4 h-4 inline opacity-40" />
                                            </span>
                                            {'\n'}
                                        </span>
                                    );
                                }

                                return (
                                    <span
                                        key={index}
                                        ref={el => charRefs.current[index] = el}
                                        className={`${statusClass} ${wordClass} transition-colors duration-75 rounded-sm`}
                                        onClick={(e) => { e.stopPropagation(); handleWordClick(item.wordId); }}
                                    >
                                        {(index < userInput.length && userInput[index] === item.char) ? item.char : (item.displayChar || item.char)}
                                    </span>
                                );
                            }))}
                        {difficulty !== 'xtra' && (
                            <div className="absolute bg-indigo-500 dark:bg-indigo-400 w-[2px] transition-all duration-75 ease-out z-20 animate-pulse pointer-events-none" style={{ left: cursorStyle.left, top: cursorStyle.top, height: cursorStyle.height }} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TypingView;
